import React from 'react';
import styled from 'styled-components';
import { DropDown } from 'components/atoms/DropDownContainer';
import { PrevIconCalendar } from 'components/atoms/PrevIconCalendar';
import { NextIconCalendar } from 'components/atoms/NextIconCalendar';
import PrevArrowIcon from 'assets/chevron-left';
import NextArrowIcon from 'assets/chevron-right';
import DateRangePicker from 'components/DateRangePicker';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'WindowDimensionProvider';
import { noop } from 'lodash';

const Footer = styled.div`
  display: flex;
  padding: 14px;
  padding-top: 0px;
  gap: 12px;
`;

const FooterSpacer = styled.div`
  flex: 1;
`;

const CancelButton = styled.button`
  height: 40px;
  padding: 10px 18px;
  border: none;
  background-color: var(--way-palette-white-100);
  color: var(--way-colors-primaryColorShades-100);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button`
  height: 40px;
  padding: 10px 14px;
  border: none;
  background-color: var(--way-colors-buttonColorShades-100);
  color: var(--way-palette-white-100);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface DateRangeCalendarDropDownProps {
  dropDownRef: React.RefObject<HTMLDivElement>;
  endDateCalendar: Date | null;
  handleClose: () => void;
  handleOnDateChange: ({
    startDate,
    endDate,
  }: {
    startDate: Date | null;
    endDate: Date | null;
  }) => void;
  isDayHighlighted: (date: Date) => boolean;
  position: 'left' | 'right';
  showDropDown: boolean;
  startDateCalendar: Date | null;
  onPrevMonthClick?: () => void;
  onNextMonthClick?: () => void;
}

const DateRangeCalendarDropDown = ({
  dropDownRef,
  endDateCalendar,
  handleClose,
  handleOnDateChange,
  isDayHighlighted,
  position,
  showDropDown,
  startDateCalendar,
  onPrevMonthClick,
  onNextMonthClick,
}: DateRangeCalendarDropDownProps) => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();
  const showClearButton = startDateCalendar && endDateCalendar;
  const clearPicker = () => {
    handleOnDateChange({ startDate: null, endDate: null });
  };
  const cancelPicker = () => {
    handleOnDateChange({ startDate: null, endDate: null });
    handleClose();
  };
  const applyDateFilter = () => {
    handleOnDateChange({
      startDate: startDateCalendar,
      endDate: endDateCalendar,
    });
    handleClose();
  };
  if (!showDropDown) {
    return null;
  }
  return (
    <DropDown
      ariaLabel={translate('dateRangeSelector')}
      dropDownRef={dropDownRef}
      position={position}
      role="dialog"
    >
      <DateRangePicker
        startDate={startDateCalendar}
        endDate={endDateCalendar}
        onChange={handleOnDateChange}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        noBorder
        onOutsideClick={handleClose}
        isDayHighlighted={isDayHighlighted}
        isOutsideRange={(date: Date) => !isDayHighlighted(date)}
        onPrevMonthClick={onPrevMonthClick}
        onNextMonthClick={onNextMonthClick}
        navPrev={
          <PrevIconCalendar
            aria-label="Previous month"
            tabIndex={0}
            role="button"
          >
            <PrevArrowIcon color="var(--way-palette-black-100)" />
          </PrevIconCalendar>
        }
        navNext={
          <NextIconCalendar aria-label="Next month" tabIndex={0} role="button">
            <NextArrowIcon color="var(--way-palette-black-100)" />
          </NextIconCalendar>
        }
      />
      <Footer>
        {showClearButton && (
          <CancelButton
            className="date-range-calendar-dropdown-clear-picker-button"
            onClick={clearPicker}
            disabled={!startDateCalendar && !endDateCalendar}
            tabIndex={0}
          >
            {translate('clear')}
          </CancelButton>
        )}
        <FooterSpacer />
        <CancelButton
          className="date-range-calendar-dropdown-cancel-picker-button"
          type="button"
          onClick={cancelPicker}
          tabIndex={0}
        >
          {translate('cancel')}
        </CancelButton>
        <ConfirmButton
          className="date-range-calendar-dropdown-apply-picker-button"
          type="button"
          onClick={
            startDateCalendar && endDateCalendar ? applyDateFilter : noop
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' && startDateCalendar && endDateCalendar) {
              e.preventDefault();
              e.stopPropagation();
              applyDateFilter();
            }
          }}
          disabled={!startDateCalendar || !endDateCalendar}
          tabIndex={0}
        >
          {translate('apply')}
        </ConfirmButton>
      </Footer>
    </DropDown>
  );
};

export default DateRangeCalendarDropDown;
